import { useRouteMatcher, useRoutes, useUrlGenerator } from '@folklore/routes';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';

import { useHasPermission, useHasJoinedGroup, useIsAuthor } from '../../contexts/AuthContext';
import { usePromotions } from '../../contexts/PromotionsContext';
// import { useAuthLogout } from '../../hooks/useAuth';

import RoundedButton from '../buttons/RoundedButton';

import styles from '../../styles/menus/account-menu.module.css';

interface AccountMenuProps {
    className?: string | null;
}

function AccountMenu({ className = null }: AccountMenuProps) {
    const url = useUrlGenerator();

    const routes = useRoutes();
    const routeMatcher = useRouteMatcher();
    const hasAttributionAccess = useHasPermission('attribution');
    const isPro = useHasJoinedGroup('pro');
    const isAuthor = useIsAuthor();

    const { promotions = [] } = usePromotions();
    const filteredPromotions = (promotions || [])
        .filter(({ placement }) => placement === 'account')
        .slice(0, 6);

    const items = [
        {
            id: 'account',
            route: 'account',
            label: isPro ? (
                <FormattedMessage defaultMessage="Compte <sup>PRO</sup>" description="Menu item" />
            ) : (
                <FormattedMessage defaultMessage="Compte" description="Menu item" />
            ),
            icon: (
                <span className={classNames(['material-symbols-rounded', styles.icon])}>home</span>
            ),
        },
        {
            id: 'profile',
            route: 'account.profile',
            label: <FormattedMessage defaultMessage="Profil" description="Menu item" />,
            icon: (
                <span className={classNames(['material-symbols-rounded', styles.icon])}>
                    person
                </span>
            ),
        },
        {
            id: 'subscriptions',
            route: 'account.subscriptions',
            label: <FormattedMessage defaultMessage="Abonnements" description="Menu item" />,
            icon: (
                <span className={classNames(['material-symbols-rounded', styles.icon])}>mail</span>
            ),
        },
        filteredPromotions.length > 0
            ? {
                  id: 'promotions',
                  route: 'account.promotions',
                  label: <FormattedMessage defaultMessage="Avantages" description="Menu item" />,
                  icon: (
                      <span className={classNames(['material-symbols-rounded', styles.icon])}>
                          redeem
                      </span>
                  ),
              }
            : null,
        {
            id: 'timeline',
            route: 'account.timeline',
            label: <FormattedMessage defaultMessage="Historique" description="Menu item" />,
            icon: (
                <span className={classNames(['material-symbols-rounded', styles.icon])}>
                    amp_stories
                </span>
            ),
        },
        hasAttributionAccess || isPro || isAuthor
            ? {
                  id: 'stats',
                  route: 'account.stats',
                  label: <FormattedMessage defaultMessage="Statistiques" description="Menu item" />,
                  icon: (
                      <span className={classNames(['material-symbols-rounded', styles.icon])}>
                          area_chart
                      </span>
                  ),
              }
            : null,
    ].filter((it) => it !== null);

    return (
        <div className={classNames([styles.container, className])}>
            <div className={styles.inner}>
                {items.map(
                    ({
                        id,
                        route = null,
                        label,
                        icon = null,
                        // className: buttonClassName,
                        ...props
                    }) => {
                        const [match = false] = route !== null ? routeMatcher(routes[route]) : [];
                        return (
                            <RoundedButton
                                key={id || route}
                                href={route !== null ? url(route) : null}
                                className={classNames([
                                    styles.button,
                                    [styles[id]],
                                    // buttonClassName,
                                ])}
                                compact
                                active={match === true}
                                {...props}
                            >
                                {icon}
                                <span className={styles.label}>{label}</span>
                            </RoundedButton>
                        );
                    },
                )}
            </div>
        </div>
    );
}

export default AccountMenu;
